"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import type { Contact } from "@/lib/config";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon";
import {
  PhoneIcon,
  MailIcon,
  InstagramIcon,
  FacebookIcon,
} from "@/components/icons/ContactIcons";

// Footer contact column. Renders the build-time values immediately (no flash),
// then refreshes from /api/contact so runtime env changes (CONTACT_*) show up
// even though the public pages are statically cached. `address` is the
// admin-editable, already-localized postal address (from Settings).
export function ContactColumn({
  initial,
  address,
}: {
  initial: Contact;
  address?: string;
}) {
  const t = useTranslations("Footer");
  const tContact = useTranslations("Contact");
  const [c, setC] = useState(initial);

  useEffect(() => {
    let alive = true;
    fetch("/api/contact")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (alive && data) setC(data as Contact);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-copper">
        {t("contactTitle")}
      </h3>
      <ul className="mt-4 space-y-2.5 text-sm text-stone">
        <li>
          <a
            href={`tel:${c.phone}`}
            className="inline-flex items-center gap-1.5 hover:text-forest-600"
          >
            <PhoneIcon className="h-4 w-4 shrink-0 text-forest-600" />
            {tContact("call")}: {c.phone}
          </a>
        </li>
        <li>
          <a
            href={`https://wa.me/${c.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 hover:text-forest-600"
          >
            <WhatsAppIcon className="h-4 w-4 shrink-0 text-[#25D366]" />
            WhatsApp: {c.phone}
          </a>
        </li>
        <li>
          <a
            href={`mailto:${c.email}`}
            className="inline-flex items-center gap-1.5 hover:text-forest-600"
          >
            <MailIcon className="h-4 w-4 shrink-0 text-forest-600" />
            {c.email}
          </a>
        </li>
        {address && (
          <li className="flex items-start gap-1.5 pt-1">
            <span aria-hidden>📍</span>
            <span>{address}</span>
          </li>
        )}
        <li className="flex flex-wrap gap-4 pt-1">
          {c.instagram && (
            <a
              href={c.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 hover:text-forest-600"
            >
              <InstagramIcon className="h-4 w-4 shrink-0 text-[#E4405F]" />
              Instagram
            </a>
          )}
          {c.facebook && (
            <a
              href={c.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 hover:text-forest-600"
            >
              <FacebookIcon className="h-4 w-4 shrink-0 text-[#1877F2]" />
              Facebook
            </a>
          )}
        </li>
      </ul>
    </div>
  );
}
